import type { FC } from 'react';
import React, { useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import GraphLineIcon from '@atlaskit/icon/glyph/graph-line';

import { DialogsStateContainer } from '@confluence/dialogs';
import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils';

import { getAutomationSlide } from './getAutomationSlide';
import { getWhiteboardsSlide } from './getWhiteboardsSlide';
import type { OnboardingSlide } from './OnboardingModal';
import { OnboardingModal } from './OnboardingModal';
import Analytics from './images/Analytics.svg';
import UniverseLight from './images/Universe-Light.svg';
import UniverseDark from './images/Universe-Dark.svg';

export const i18n = defineMessages({
	'slide-1-title': {
		id: `premium-onboarding.user.onboarding.slide1.title`,
		description: `Title of the 1st slide for Premium user onboarding`,
		defaultMessage: `Welcome to Confluence Cloud Premium`,
	},
	'slide-2-title': {
		id: `premium-onboarding.user.onboarding.slide2.title`,
		description: `Title of the 2nd slide for Premium user onboarding`,
		defaultMessage: `Create world-class content with analytics`,
	},
	'slide-1-body': {
		id: `premium-onboarding.user.onboarding.slide1.body`,
		description: `Body of the 1st slide for Premium user onboarding`,
		defaultMessage: `Scale effortlessly as your organization grows. You’ve got more analytics, new permissions tools, and bulk archiving to manage your content at scale.`,
	},
	'slide-2-body-p1': {
		id: `premium-onboarding.user.onboarding.slide2.body.p1`,
		description: `1st paragraph of the 2nd slide's body for Premium user onboarding`,
		defaultMessage: `Use page and space analytics to find your most popular content and build on your success. Open {analytics} at the top of any page.`,
	},
	'slide-2-body-p2': {
		id: `premium-onboarding.user.onboarding.slide2.body.p2`,
		description: `2nd paragraph of the 2nd slide's body for Premium user onboarding. {confluenceCloudPremium} links to learn about confluence cloud plans documentation and it's message is translated in 'premium-onboarding.user.onboarding.confluence.cloud.premium'.`,
		defaultMessage: `You’ve got analytics with {confluenceCloudPremium}. Take team knowledge to the next level!`,
	},
	'learn-more': {
		id: `premium-onboarding.user.onboarding.learn.more`,
		description: `Phrase that prompts the user to learn more about Confluence Premium`,
		defaultMessage: `Learn more.`,
	},
	'confluence-cloud-premium': {
		id: `premium-onboarding.user.onboarding.confluence.cloud.premium`,
		description: `Link that takes the user to Confluence Cloud plans`,
		defaultMessage: `Confluence Cloud Premium`,
	},
	'slide-2-automate-title': {
		id: `premium-onboarding.user.onboarding.slide2.automate.title`,
		description: `Title of the NEW 2nd slide for Premium user onboarding`,
		defaultMessage: `Automation keeps content organized`,
	},
	'slide-2-automate-body': {
		id: `premium-onboarding.user.onboarding.slide2.automate.body`,
		description: `Body of the NEW 2nd slide for Premium user onboarding.`,
		defaultMessage: `<automationLink>Automate</automationLink> recurring to-dos and processes in any space where you’re an admin, including your personal space. This keeps spaces tidy and content up-to-date.`,
	},
	'slide-whiteboards-title': {
		id: `premium-onboarding.user.onboarding.slide.whiteboards.title`,
		description: `Title that summarizes features the new features available in Confluence Whiteboards`,
		defaultMessage: `Whiteboard features`,
	},
	'slide-whiteboards-body': {
		id: `premium-onboarding.user.onboarding.slide.whiteboards.body`,
		description: `Body/description of the features the new features available in Confluence Whiteboards`,
		defaultMessage: `Unlock access to advanced whiteboarding features like richer integrations with Jira and voting! <learnMoreLink>Learn more</learnMoreLink>`,
	},
});

type PremiumUserOnboardingProps = {
	stop?: () => void;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledAnalyticsIcon = styled.span({
	paddingTop: token('space.050', '4px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledConfluencePremiumLink = styled.a({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':focus': {
		outline: 'none',
	},
});

export const PremiumUserOnboarding: FC<PremiumUserOnboardingProps> = ({ stop }) => {
	const [dialogOpen, setDialogOpen] = useState(false);

	const { isWhiteboardFeatureEnabled } = useIsWhiteboardFeatureEnabled();

	const ONBOARDING_SLIDES: OnboardingSlide[] = [
		{
			title: <FormattedMessage {...i18n['slide-1-title']} />,
			body: <FormattedMessage {...i18n['slide-1-body']} />,
			imageUrlLight: UniverseLight,
			imageUrlDark: UniverseDark,
		},
		getAutomationSlide(i18n['slide-2-automate-title'], i18n['slide-2-automate-body'], 'user'),
		{
			title: <FormattedMessage {...i18n['slide-2-title']} />,
			body: (
				<>
					<FormattedMessage
						{...i18n['slide-2-body-p1']}
						tagName="p"
						values={{
							analytics: (
								<span>
									<StyledAnalyticsIcon>
										<GraphLineIcon label="analytics" size="small" />
									</StyledAnalyticsIcon>
									{/* add a space between the icon and text */}{' '}
									<FormattedMessage
										id="premium-onboarding.user.onboarding.analytics"
										description="The word analytics in bold"
										defaultMessage="Analytics"
										tagName="strong"
									/>
								</span>
							),
						}}
					/>
					<FormattedMessage
						{...i18n['slide-2-body-p2']}
						tagName="p"
						values={{
							confluenceCloudPremium: (
								<StyledConfluencePremiumLink
									href="https://confluence.atlassian.com/x/W6T0OQ"
									target="_blank"
								>
									<FormattedMessage {...i18n['confluence-cloud-premium']} />
								</StyledConfluencePremiumLink>
							),
						}}
					/>
				</>
			),
			imageUrlLight: Analytics,
			imageUrlDark: Analytics,
		},
		...(isWhiteboardFeatureEnabled('whiteboardPaywall')
			? [
					getWhiteboardsSlide(
						i18n['slide-whiteboards-title'],
						i18n['slide-whiteboards-body'],
						'user',
					),
				]
			: []),
	];

	return (
		<Subscribe to={[DialogsStateContainer]}>
			{(dialogs: DialogsStateContainer) => {
				if (!dialogOpen) {
					setDialogOpen(true);
					dialogs.showDialog(OnboardingModal, {
						slides: ONBOARDING_SLIDES,
						onClose: () => {
							if (stop) {
								stop(); // tells EP's CoordinationClient to stop showing onboarding again
							} else {
								throw new Error('PremiumUserOnboarding should be a child of PremiumOnboarding');
							}
						},
					});
				}
				return null;
			}}
		</Subscribe>
	);
};
